import { createContext, useContext, useState, type ReactNode } from "react"
import type { Step } from "./types"
import { useGetProspectQuery } from "@apis/prospects/useProspects"
import type { IProspect } from "@commonTypes/prospect"
import type { IOffer, IOffersRequest } from "@commonTypes/offer"
import type { IAddress } from "@commonTypes/address"
import type { IShopper } from "@apis/shoppers/types"
import type { IMarket } from "@apis/accounts/types"
import type { IOrder } from "@commonTypes/order"
import type { ITpvType } from "@apis/sales/types"

type NewOrderFunnelContextType = {
  step: Step
  setStep: (step: Step) => void
  prospect_guid?: string
  prospect: IProspect | undefined
  offersParams?: IOffersRequest
  setOffersParams: (offer_params: IOffersRequest | undefined) => void
  selectedAddress?: IAddress
  setSelectedAddress: (address?: IAddress) => void
  selectedMarket?: IMarket
  setSelectedMarket: (market?: IMarket) => void
  selectedRevenueClassCode?: string
  setSelectedRevenueClassCode: (revenue_class?: string) => void
  selectedOffer?: IOffer
  setSelectedOffer: (offer?: IOffer) => void
  shopper?: IShopper
  setShopper: (shopper?: IShopper) => void
  tpv: ITpvType | null
  setTpv: (tpvType: ITpvType) => void
  order?: IOrder
  setOrder: (order: IOrder) => void
  verificationCompleted: boolean
  setVerificationCompleted: (completed: boolean) => void
}

const NewOrderFunnelContext = createContext<
  NewOrderFunnelContextType | undefined
>(undefined)

export const NewOrderFunnelProvider = ({
  children,
  prospect_guid,
}: {
  children: ReactNode
  prospect_guid?: string
}) => {
  const [step, setStep] = useState<Step>("chooseOffer")
  const [offersParams, setOffersParams] = useState<IOffersRequest | undefined>(
    undefined,
  )
  const [selectedAddress, setSelectedAddress] = useState<IAddress | undefined>(
    undefined,
  )
  const [selectedMarket, setSelectedMarket] = useState<IMarket | undefined>(
    undefined,
  )
  const [selectedRevenueClassCode, setSelectedRevenueClassCode] = useState<
    string | undefined
  >(undefined)
  const [selectedOffer, setSelectedOffer] = useState<IOffer | undefined>(
    undefined,
  )
  const [shopper, setShopper] = useState<IShopper | undefined>(undefined)
  const [tpv, setTpv] = useState<ITpvType | null>(null)
  const [order, setOrder] = useState<IOrder | undefined>(undefined)
  const [verificationCompleted, setVerificationCompleted] = useState(false)

  const getProspectQuery = useGetProspectQuery(prospect_guid)

  return (
    <NewOrderFunnelContext.Provider
      value={{
        step,
        setStep,
        prospect_guid,
        prospect: getProspectQuery.data,
        offersParams,
        setOffersParams,
        selectedAddress,
        setSelectedAddress,
        selectedMarket,
        setSelectedMarket,
        selectedRevenueClassCode,
        setSelectedRevenueClassCode,
        selectedOffer,
        setSelectedOffer,
        shopper,
        setShopper,
        tpv,
        setTpv,
        order,
        setOrder,
        verificationCompleted,
        setVerificationCompleted,
      }}
    >
      {children}
    </NewOrderFunnelContext.Provider>
  )
}

export const useNewOrderFunnel = () => {
  const context = useContext(NewOrderFunnelContext)
  if (!context) {
    throw new Error(
      "useNewOrderFunnel must be used within a NewOrderFunnelProvider",
    )
  }
  return context
}
